import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import {
  getDvSourceImageUrl,
  getLandingPageTitleColor,
} from "../../functions/themeAssets";
import "../fonts/breuer-headline.css";
import "./unscrambleStage.css";
import "./crosswordStage.css";
import "./stageActions.css";
import Swal from "sweetalert2";
import UnscrambleRow from "./UnscrambleRow";
import StageCorrectPopup from "./StageCorrectPopup";
import {
  parseMinSubmitCount,
  parseShowClue,
  parseUnscrambleFromTheme,
} from "./unscrambleUtils";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";
import { showStageCompleteCelebration } from "../../functions/stageCelebration";
import {
  fireStageHelpSwal,
  getStageRulesFromTheme,
  lockPageScrollForStageModal,
  unlockPageScrollForStageModal,
} from "../../functions/stageRules";

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fireClueOrHintSwal(title, message) {
  lockPageScrollForStageModal();

  Swal.fire({
    html: `
      <div class="unscramble-clue-card">
        <h2 class="unscramble-clue-card__title">${escapeHtml(title)}</h2>
        <p class="unscramble-clue-card__text">${escapeHtml(message)}</p>
        <button type="button" class="unscramble-clue-card__btn" data-unscramble-clue-close>
          OK
        </button>
      </div>
    `,
    showConfirmButton: false,
    showCloseButton: false,
    position: "center",
    width: "min(92vw, 26em)",
    heightAuto: true,
    scrollbarPadding: false,
    returnFocus: false,
    customClass: {
      popup: "unscramble-clue-popup",
      htmlContainer: "unscramble-clue-popup__html",
    },
    showClass: { popup: "unscramble-clue-popup--in" },
    didOpen: () => {
      Swal.getPopup()
        ?.querySelector("[data-unscramble-clue-close]")
        ?.addEventListener("click", () => Swal.close(), { once: true });
    },
    didClose: () => {
      unlockPageScrollForStageModal();
    },
  });
}

export default function Stage3() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage3Points, setStage3Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [solvedRows, setSolvedRows] = useState(() => new Set());
  const [showCorrectPopup, setShowCorrectPopup] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const solvedRef = useRef(new Set());
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const stage3CompleteRef = useRef(false);
  const rowRefs = useRef([]);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = storedUser.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const entries = useMemo(
    () => parseUnscrambleFromTheme(themeData),
    [themeData]
  );

  const showClue = useMemo(() => parseShowClue(themeData), [themeData]);

  const minSubmitCount = useMemo(
    () => parseMinSubmitCount(themeData, entries.length),
    [themeData, entries.length]
  );

  const pointsPerWord = useMemo(() => {
    const p = Number(themeData?.points);
    return Number.isFinite(p) && p > 0 ? p : 10;
  }, [themeData?.points]);

  const title = useMemo(() => {
    const t =
      themeData?.unscramble_title ||
      themeData?.landing_page_title ||
      themeData?.main_title ||
      themeData?.themename ||
      "Unscramble";
    return String(t).trim();
  }, [themeData]);

  const subtitle = useMemo(() => {
    const custom =
      themeData?.unscramble_subtitle || themeData?.unscramble_instructions;
    if (custom && String(custom).trim()) return String(custom).trim();
    return "Unscramble the words to discover key skills that help women unlock opportunities and grow personally and professionally.";
  }, [themeData]);

  const titleTextColor = useMemo(
    () => getLandingPageTitleColor(themeData),
    [themeData]
  );

  const themedTextStyle = useMemo(
    () => (titleTextColor ? { color: titleTextColor } : undefined),
    [titleTextColor]
  );

  const canSubmit = solvedRows.size >= minSubmitCount;

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch]);

  useEffect(() => {
    document.body.classList.add("unscramble-page");
    return () => {
      document.body.classList.remove("unscramble-page");
    };
  }, []);

  useEffect(() => {
    solvedRef.current = solvedRows;
  }, [solvedRows]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage3Points);
  }, [stage3Points]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      if (themeStatus !== "succeeded" || !entries.length) return;

      const uid =
        storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        const rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, 3);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage3 = Number(st.stage3) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage3);
          setStage3Points(dbStage3);
          setDisplayTotalScore(dbTotal);

          const solvedFromScore =
            pointsPerWord > 0
              ? Math.min(
                  entries.length,
                  Math.floor(dbStage3 / pointsPerWord)
                )
              : 0;
          if (solvedFromScore > 0) {
            const restored = new Set(
              Array.from({ length: solvedFromScore }, (_, i) => i)
            );
            setSolvedRows(restored);
            solvedRef.current = restored;
          }
        }

        setStageActive(3, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Stage3 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [
    themeStatus,
    entries.length,
    backendBase,
    adminToken,
    isDemoBypass,
    storedUser,
    pointsPerWord,
    navigate,
  ]);

  const finishStage = useCallback(
    async (skip = false) => {
      if (submitting || submittedRef.current) return;
      if (!skip && !canSubmit) {
        Swal.fire({
          icon: "info",
          title: "Not enough answers",
          text: `Solve at least ${minSubmitCount} word(s) before submitting.`,
        });
        return;
      }

      submittedRef.current = true;
      setSubmitting(true);

      const uid =
        storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;
      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stage3Score = skip ? 0 : stage3Points;
      const stageId = sessionStorage.getItem(STAGE_ROW_KEY);
      const finalTotal = otherStagesTotalRef.current + stage3Score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: 3,
          score: stage3Score,
          time: timeStr,
          total_score: finalTotal,
        });
        if (saveResult.total_score != null) {
          setDisplayTotalScore(saveResult.total_score);
        }
      }

      clearStageActive(3);
      markStageCompleted(3, storedUser);

      await showStageCompleteCelebration({
        stageNumber: 3,
        skip,
        score: stage3Score,
      });

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      navigate("/stage4");
    },
    [
      submitting,
      canSubmit,
      minSubmitCount,
      stage3Points,
      storedUser,
      isDemoBypass,
      backendBase,
      adminToken,
      navigate,
    ]
  );

  const handleRowSolved = useCallback(
    (rowIndex) => {
      if (solvedRef.current.has(rowIndex)) return;

      setSolvedRows((prev) => {
        const next = new Set(prev);
        next.add(rowIndex);
        return next;
      });

      setStage3Points((p) => p + pointsPerWord);

      const correctPopupImg = themeData?.puzzle_image;
      if (correctPopupImg || themeData?.Correct_ans_text_popup) {
        setShowCorrectPopup(true);
        setTimeout(() => setShowCorrectPopup(false), 1500);
      }

      const nextIndex = rowIndex + 1;
      if (nextIndex < entries.length) {
        setTimeout(() => rowRefs.current[nextIndex]?.focusFirst?.(), 0);
      }
    },
    [pointsPerWord, themeData, entries.length]
  );

  const handleShowClue = useCallback((clueText) => {
    if (!clueText) return;
    fireClueOrHintSwal("Clue", clueText);
  }, []);

  const handleHint = useCallback(() => {
    const unsolved = entries
      .map((_, i) => i)
      .filter((i) => !solvedRef.current.has(i));
    if (!unsolved.length) return;
    const idx = unsolved[Math.floor(Math.random() * unsolved.length)];
    const entry = entries[idx];
    if (entry.clue) {
      handleShowClue(entry.clue);
      return;
    }
    const answer = String(entry.answer).replace(/[^a-zA-Z0-9]/gi, "");
    if (answer.length) {
      fireClueOrHintSwal("Hint", `First letter: ${answer[0].toUpperCase()}`);
    }
  }, [entries, handleShowClue]);

  const showHelp = () => {
    fireStageHelpSwal({
      rules: getStageRulesFromTheme(themeData, "unscramble_rules"),
      themeData,
    });
  };

  if (themeStatus === "loading" || themeStatus === "idle" || !dbReady) {
    return <div className="unscramble-stage__loading">Loading game…</div>;
  }

  if (!entries.length) {
    return (
      <div className="unscramble-stage__empty">
        No unscramble words configured. Add them in theme admin under Unscramble.
      </div>
    );
  }

  const correctPopupImg = themeData?.puzzle_image;

  return (
    <div className="unscramble-stage">
      <header className="unscramble-stage__header">
        <div className="unscramble-stage__title-block">
          <span className="unscramble-stage__badge">STAGE 3</span>
          <h1 className="unscramble-stage__title" style={themedTextStyle}>
            {title}
          </h1>
          <p className="unscramble-stage__subtitle" style={themedTextStyle}>
            {subtitle}
          </p>
        </div>
        <div className="unscramble-stage__hud">
          <div className="unscramble-stage__stats-card">
            <div className="unscramble-stage__stat">
              <span aria-hidden>⏱</span>
              <span>{formatSecondsToClock(elapsedSec)}</span>
            </div>
            <div className="unscramble-stage__stat">
              <span aria-hidden>★</span>
              <span>{displayTotalScore}</span>
            </div>
          </div>
          <div className="stage-hud-actions">
            <button
              type="button"
              className="crossword-stage__help-btn"
              aria-label="How to play"
              onClick={showHelp}
            >
              ?
            </button>
            <button
              type="button"
              className="game-stage-actions__skip-btn game-stage-actions__skip-btn--hud"
              disabled={submitting}
              onClick={() => finishStage(true)}
            >
              Skip
            </button>
          </div>
        </div>
      </header>

      <div className="unscramble-stage__content">
        <div className="unscramble-stage__list">
          {entries.map((entry, i) => (
            <UnscrambleRow
              key={`${entry.scrambled}-${i}`}
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              index={i}
              scrambled={entry.scrambled}
              answer={entry.answer}
              clue={entry.clue}
              showClueButton={showClue && Boolean(entry.clue)}
              done={solvedRows.has(i)}
              onSolved={handleRowSolved}
              onShowClue={handleShowClue}
              textStyle={themedTextStyle}
            />
          ))}
        </div>

        <footer className="unscramble-stage__footer">
          <button
            type="button"
            className="unscramble-stage__submit-btn"
            disabled={!canSubmit || submitting}
            onClick={() => finishStage(false)}
          >
            SUBMIT
          </button>
        </footer>
      </div>

      <StageCorrectPopup
        open={
          showCorrectPopup &&
          Boolean(correctPopupImg || themeData?.Correct_ans_text_popup)
        }
        imageUrl={
          correctPopupImg ? getDvSourceImageUrl(correctPopupImg) : ""
        }
        text={themeData?.Correct_ans_text_popup || ""}
      />
    </div>
  );
}
